import moment from 'moment';
import { ICustomerOnboardingTimeline, IStageDurations } from './typed';
import {
  allStages,
  calcInterval,
  DRY_RUN_CUSTOMER_ID,
  FUNNEL_SHOW_LIMIT,
  funnelFieldMap,
  funnelStages,
  INTEGRATED_BUT_NOT_VISITED_PULSE_STAGE,
  INTEGRATED_STAGE,
  NON_INTEGRATED_STAGE,
  STARTED_SELF_SERVE_STAGE,
  USED_TRENDS_STAGE,
  WAITING_ACTIVATED_STAGE,
} from './constant';

export const isDryRunCustomer = (customerId: number) => {
  return DRY_RUN_CUSTOMER_ID.includes(customerId);
};

export const safeArrayLength = (list: any[]) => Math.max(0, list?.length ?? 0);

export const formatDate = (time: string) => {
  return time ? moment(time).format('YYYY/MM/DD HH:mm') : '-';
};

function calculateStageDurations(customer: ICustomerOnboardingTimeline): IStageDurations {
  const { start_self_serve_at, access_welcome_page_at, receive_data_at, access_trends_page_at } =
    customer;

  const formatDuration = (minutes: number): string => {
    const days = Math.floor(minutes / 1440); // 1440 minutes in a day
    const hours = Math.floor((minutes % 1440) / 60);
    const mins = minutes % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);

    return parts.join(' ');
  };

  const diffInMinutes = (from?: string | null, to?: string | null): string | null => {
    if (!from) return null;

    const fromTime = moment(from);
    const toTime = moment(to ?? moment());

    const totalMinutes = toTime.diff(fromTime, 'minutes');
    return formatDuration(totalMinutes);
  };

  return {
    [WAITING_ACTIVATED_STAGE]: diffInMinutes(start_self_serve_at, access_welcome_page_at),
    [NON_INTEGRATED_STAGE]: diffInMinutes(access_welcome_page_at, receive_data_at),
    [INTEGRATED_BUT_NOT_VISITED_PULSE_STAGE]: diffInMinutes(receive_data_at, access_trends_page_at),
  };
}

function filterCustomersByStageAndTime(
  data: ICustomerOnboardingTimeline[],
  stage: string,
  shortcut?: string,
): ICustomerOnboardingTimeline[] {
  let startDate: moment.Moment | null = null;
  let endDate: moment.Moment | null = null;

  // If a shortcut is provided, the parsing time range
  if (shortcut) {
    const range = calcInterval(shortcut);

    if (range && moment.isMoment(range[0]) && moment.isMoment(range[1])) {
      [startDate, endDate] = range;
    } else {
      return [];
    }
  }

  const isInTimeRange = (date: string | null) => {
    if (!date) return false;
    if (!startDate || !endDate) return true; // If no shortcut is passed, no time judgment will be made
    const momentDate = moment(date);
    return momentDate.isBetween(startDate, endDate, null, '[]');
  };

  // Screen by stage and time range
  return data.filter((account) => {
    const { start_self_serve_at, access_welcome_page_at, receive_data_at, access_trends_page_at } =
      account;

    switch (stage) {
      // metrics table
      case WAITING_ACTIVATED_STAGE:
        return (
          !!start_self_serve_at && !access_welcome_page_at && isInTimeRange(start_self_serve_at)
        );

      case NON_INTEGRATED_STAGE:
        return (
          !!access_welcome_page_at && !receive_data_at && isInTimeRange(access_welcome_page_at)
        );

      case INTEGRATED_BUT_NOT_VISITED_PULSE_STAGE:
        return !!receive_data_at && !access_trends_page_at && isInTimeRange(receive_data_at);

      // conversion funnel
      case STARTED_SELF_SERVE_STAGE:
        return !!start_self_serve_at && isInTimeRange(start_self_serve_at);

      case INTEGRATED_STAGE:
        return (
          !!receive_data_at && isInTimeRange(start_self_serve_at) && isInTimeRange(receive_data_at)
        );

      case USED_TRENDS_STAGE:
        return (
          !!access_trends_page_at &&
          isInTimeRange(start_self_serve_at) &&
          isInTimeRange(receive_data_at) &&
          isInTimeRange(access_trends_page_at)
        );
      default:
        return false;
    }
  });
}

export function getAllStageCustomersByTimeRange(
  data?: ICustomerOnboardingTimeline[],
  shortcut?: string,
): Record<string, (ICustomerOnboardingTimeline & { stageDurationDays: IStageDurations })[]> {
  if (!data) return {};

  const dataWithDurations = data.map((c) => ({
    ...c,
    stageDurationDays: calculateStageDurations(c),
  }));

  return allStages.reduce((result, stage) => {
    result[stage] = filterCustomersByStageAndTime(dataWithDurations, stage, shortcut);
    return result;
  }, {});
}

export const buildSelfServeBarChartOption = ({
  seriesData,
}: {
  seriesData: { value: number; customers: ICustomerOnboardingTimeline[] }[];
}) => {
  const maxValue = Math.max(...seriesData.map((d) => d.value));
  // Set the maximum value of the Y-axis to be no less than a minimum multiple of 10 of the maxValue and no less than 30
  const finalMax = Math.ceil(Math.max(maxValue, 30) / 10) * 10;

  return {
    graphic: {
      elements: [
        {
          type: 'text' as const,
          left: '2%',
          top: '40%',
          style: {
            text: 'Accounts Number',
            fontSize: 14,
            fill: '#333',
            textAlign: 'center',
          },
          cursor: 'default',
          rotation: Math.PI / 2,
          originX: 0,
          originY: 0,
        },
      ],
    },
    tooltip: {
      trigger: 'axis' as const,
      formatter: (params: any) => {
        const data = params[0]?.data;
        const stage = params[0]?.name;

        if (!data || !data.customers) return '';

        const field = funnelFieldMap[stage];

        const visibleCustomers = data.customers.slice(0, FUNNEL_SHOW_LIMIT);
        const moreCount = data.customers.length - FUNNEL_SHOW_LIMIT;

        const customersHtml = visibleCustomers
          .map((c: ICustomerOnboardingTimeline) => {
            const time = c?.[field] ? formatDate(c[field] as string) : '-';
            return `
              <div>
                <strong>${c.customer_name}</strong>
                <span> / </span>
                <span style="color: #888; font-size: 12px;">${time}</span>
              </div>
            `;
          })
          .join('');

        const moreText =
          moreCount > 0
            ? `<div style="
              color: #1890ff;
              font-size: 13px;
              font-weight: 500;
              margin-top: 4px;
            ">
              ...and ${moreCount} more — click the bar to view all
           </div>`
            : '';

        return `
          <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
            <strong>${stage}</strong><br/>
            <span>Number: ${data.value}</span><br/><br/>
            ${customersHtml}
            ${moreText}
          </div>
      `;
      },
    },
    xAxis: {
      type: 'category' as const,
      data: funnelStages,
      axisLabel: {
        fontSize: 12,
        formatter: (value: string) => {
          const maxLineLength = 45;
          const words = value.split(' ');
          let line: string = '';
          const lines: string[] = [];

          words.forEach((word) => {
            if ((line + ' ' + word).trim().length > maxLineLength) {
              lines.push(line.trim());
              line = word;
            } else {
              line += ' ' + word;
            }
          });
          if (line) lines.push(line.trim());

          return lines.join('\n');
        },
      },
    },
    yAxis: {
      type: 'value' as const,
      min: 0,
      max: finalMax,
      interval: 10,
      axisLabel: {
        fontSize: 12,
      },
    },
    series: [
      {
        type: 'bar' as const,
        data: seriesData,
        barWidth: 40,
        itemStyle: {
          color: '#1093F5',
        },
      },
    ],
    grid: {
      left: '4%',
      right: '2%',
      bottom: '10%',
      top: '10%',
      containLabel: true,
    },
  };
};
